import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaBuilding,
  FaChartLine,
  FaCheckCircle,
  FaCubes,
  FaDesktop,
  FaExternalLinkAlt,
  FaFilm,
  FaGlobeAmericas,
  FaHospital,
  FaHotel,
  FaLightbulb,
  FaMapMarkedAlt,
  FaPlay,
  FaProjectDiagram,
  FaSun,
  FaUnity,
  FaUtensils,
  FaVrCardboard,
  FaWalking
} from 'react-icons/fa';
import SEO, { siteUrl } from '../components/SEO';

const videoSrc = '/pixify-archi-walkthrough-demo.mp4';
const pixifyLogo = '/pixify-logo.png';

const featureCards = [
  {
    icon: FaVrCardboard,
    title: 'VR Walkthrough Mode',
    badge: 'Immersive Experience',
    description:
      'Full Virtual Reality support allows clients and buyers to step directly inside the architectural space in 1:1 true-to-life scale using modern VR headsets.'
  },
  {
    icon: FaFilm,
    title: 'Cinematic View & 20 Viewpoints',
    badge: 'Guided Camera Tour',
    description:
      'Features 20 curated architectural camera angles with automated flythroughs, previous/next scene navigation, pause controls, and smooth transition effects.'
  },
  {
    icon: FaMapMarkedAlt,
    title: 'Interactive Google Map & Teleport',
    badge: 'Geolocation HUD',
    description:
      'Integrated real-world satellite and roadmap overlay with live GPS coordinates (Beverly Hills, CA), copyable location data, and level-by-level room teleportation.'
  },
  {
    icon: FaWalking,
    title: 'First Person Controller & Minimap',
    badge: 'Real-Time Navigation',
    description:
      'Seamless keyboard (WASD) and mouse exploration paired with a live dynamic floorplan minimap (toggleable with [M]) for effortless orientation.'
  },
  {
    icon: FaSun,
    title: 'Day & Night Lighting Views',
    badge: 'Ambience Switching',
    description:
      'Instantly toggle or automatically cycle between bright daylight illumination and moody night ambience to showcase interior fixtures and exterior accents.'
  },
  {
    icon: FaUnity,
    title: 'Baked Global Illumination',
    badge: 'Unity Engine Performance',
    description:
      'High-fidelity baked lighting and ray-traced shadows deliver photo-realistic visuals while maintaining ultra-smooth 60+ FPS performance on showroom PCs.'
  }
];

const industryUseCases = [
  {
    icon: FaHotel,
    title: 'Hotels & Luxury Resorts',
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=900&q=80',
    imageAlt: 'Luxury resort and hotel architectural visualization',
    features: ['Presidential suites & villas', 'Lobby & banquet walkthroughs', 'Day/night pool & lounge mood', 'Pre-booking investor tours'],
    need: 'Hospitality investors and premium guests need to experience the luxury scale, lighting ambience, and room views long before hotel grand openings.',
    value: 'Resort developers can secure high-value brand partnerships, operator sign-offs, and advance corporate event bookings with interactive walkthroughs.',
    whyUse: 'Use this architectural engine to showcase suites, outdoor decks, and restaurant zones to global stakeholders without waiting for physical construction.'
  },
  {
    icon: FaBuilding,
    title: 'Luxury Apartments & High-Rises',
    image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=900&q=80',
    imageAlt: 'Modern high-rise residential apartment project',
    features: ['3 BHK & duplex layout review', 'Multi-floor level navigation', 'Google Map locality context', 'Pre-construction off-plan sales'],
    need: 'Home buyers hesitate when buying off-plan properties solely from 2D blueprints or static brochure renders.',
    value: 'Sales teams can walk buyers through every room, demonstrate day/night lighting, and prove location value with live Google Maps integration.',
    whyUse: 'Eliminate expensive physical sample flat construction and close buyer bookings faster with interactive first-person and VR walkthroughs.'
  },
  {
    icon: FaHospital,
    title: 'Hospitals & Healthcare Facilities',
    image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=900&q=80',
    imageAlt: 'Modern healthcare hospital architecture visualization',
    features: ['Patient ward & ICU ergonomics', 'Doctor consultation suites', 'Medical equipment spacing', 'Regulatory & board approvals'],
    need: 'Healthcare architecture demands strict spatial compliance, patient flow ergonomics, and approvals from hospital boards and medical authorities.',
    value: 'Doctors, administrators, and architects can evaluate operational pathways, room clearances, and sanitization zones before laying a single brick.',
    whyUse: 'Speed up healthcare planning approvals, avoid costly mid-construction redesigns, and present medical facilities confidently to donor boards.'
  },
  {
    icon: FaUtensils,
    title: 'Restaurants, Cafes & Lounges',
    image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=900&q=80',
    imageAlt: 'Commercial luxury restaurant interior ambience',
    features: ['Dining & bar seating flow', 'Evening & dinner mood lighting', 'Kitchen & service accessibility', 'Franchise pitch presentations'],
    need: 'Restaurateurs and franchise partners must test customer seating density, aesthetic lighting moods, and kitchen-to-table circulation.',
    value: 'Hospitality designers can pitch concepts to brand partners, test intimate evening lighting, and optimize table layouts for maximum revenue.',
    whyUse: 'Help restaurant operators visualize exact interior vibes, finishes, and spatial comfort to secure prime leases and franchise investments.'
  },
  {
    icon: FaDesktop,
    title: 'Corporate Campuses & Commercial Offices',
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=900&q=80',
    imageAlt: 'Modern commercial corporate office interior',
    features: ['Boardroom & executive suites', 'Open collaboration zones', 'Acoustic & lighting checks', 'Commercial tenant pre-leasing'],
    need: 'Commercial developers need to lease expansive office floors and tech park spaces to multinational tenants well ahead of project completion.',
    value: 'Prospective corporate tenants can explore workstation layouts, conference rooms, and reception lobbies from anywhere in the world.',
    whyUse: 'Accelerate commercial pre-leasing cycles and deliver interactive virtual tours to international corporate real estate heads.'
  }
];

const commercialOutcomes = [
  'Accelerates off-plan property sales & client approvals by up to 300%',
  'Eliminates the multi-million rupee cost of building physical sample flats',
  'Enables remote & international investor sales with full interactive clarity',
  'Provides real-world geolocation assurance with live Google Map telemetry',
  'Offers dual-mode presentation: automated cinematic tour + free-roam VR exploration'
];

const marketDrivers = [
  {
    icon: FaChartLine,
    title: 'Pre-Construction Real Estate Selling',
    description: 'Buyers and investors demand immersive digital tours before committing capital, turning interactive 3D demos into the primary sales engine.'
  },
  {
    icon: FaVrCardboard,
    title: 'Virtual Reality Adoption in ArchViz',
    description: 'VR headsets give clients an undeniable sense of physical scale, ceiling height, and room depth that 2D screens cannot replicate.'
  },
  {
    icon: FaFilm,
    title: 'Cinematic Visual Storytelling',
    description: 'Automated viewpoint tours allow sales executives to deliver effortless, high-impact presentations even without manual controller skills.'
  },
  {
    icon: FaMapMarkedAlt,
    title: 'Geospatial Context & Location Trust',
    description: 'Embedding real-time satellite imagery and GPS coordinates builds instant location trust for luxury real estate developments.'
  }
];

const technicalCapabilities = [
  'Unity 6 High-Definition Render Pipeline (HDRP)',
  '20 Custom architectural cinematic viewpoints',
  'Full VR Walkthrough mode with 6-DoF tracking',
  'Live Google Map Satellite & Roadmap integration',
  'Instant Beverly Hills GPS telemetry & copy button',
  'Multi-level room teleportation system',
  'Dynamic Day / Night & Auto Day-Night cycling',
  'Interactive HUD minimap with [M] toggle',
  'WASD first-person controller & mouse look',
  'Optimized 60+ FPS native showroom execution'
];

const PixifyArchiWalkthrough: React.FC = () => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const pixifyJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Pixify Archi Walkthrough - Interactive Unity Architectural Demo',
    url: `${siteUrl}/pixify-archi-walkthrough`,
    image: `${siteUrl}${pixifyLogo}`,
    description:
      'A luxury Unity architectural walkthrough demo built by RedRender for Pixify, featuring VR walkthrough support, 20 cinematic viewpoints, live Google Map location integration, and day/night transitions.',
    creator: {
      '@type': 'Organization',
      name: 'RedRender',
      url: siteUrl
    },
    associatedMedia: {
      '@type': 'VideoObject',
      name: 'Pixify Archi Walkthrough Demo Video',
      contentUrl: `${siteUrl}${videoSrc}`
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-black via-gray-900 to-black pt-24">
      <SEO
        title="Pixify Archi Walkthrough - VR & Cinematic Architectural Demo | RedRender"
        description="Explore the Pixify Archi Walkthrough by RedRender: features VR walkthrough mode, 20 cinematic viewpoints, interactive Google Map location integration, day/night lighting, and multi-industry applications."
        path="/pixify-archi-walkthrough"
        image={pixifyLogo}
        keywords={[
          'Pixify Archi Walkthrough',
          'Unity architectural walkthrough',
          'VR architectural walkthrough',
          'Cinematic architectural viewpoints',
          'Google Map architectural integration',
          'first person architectural demo',
          'day night transition Unity',
          'luxury real estate virtual tour',
          'hotel walkthrough visualization',
          'hospital archviz demo',
          'restaurant 3D walkthrough',
          'RedRender Pixify project'
        ]}
        jsonLd={pixifyJsonLd}
      />

      {/* Decorative Gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_12%,rgba(236,72,153,0.12),transparent_26rem),radial-gradient(circle_at_76%_20%,rgba(59,130,246,0.16),transparent_28rem),radial-gradient(circle_at_50%_85%,rgba(239,68,68,0.12),transparent_32rem),linear-gradient(rgba(255,255,255,0.012)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.012)_1px,transparent_1px)] bg-[size:auto,auto,auto,82px_82px,82px_82px]" />
      <div className="pointer-events-none absolute left-[-8rem] top-28 h-80 w-80 rounded-full bg-pink-500/10 blur-3xl" />
      <div className="pointer-events-none absolute right-[-8rem] top-[36rem] h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />

      {/* HERO SECTION */}
      <section className="relative">
        <div className="container relative z-10 mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-pink-400/30 bg-pink-400/10 px-5 py-2 backdrop-blur-sm">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-pink-400 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-pink-400" />
                </span>
                <span className="font-display text-sm font-semibold text-pink-200">Pixify Association with RedRender</span>
              </div>

              <img
                src={pixifyLogo}
                alt="Pixify Design Agency logo"
                className="mt-8 w-full max-w-[18rem] object-contain sm:max-w-sm lg:max-w-md"
              />

              <h1 className="mt-8 max-w-4xl font-display text-5xl font-bold leading-tight text-white sm:text-6xl lg:text-7xl">
                Archi Walkthrough
                <span className="mt-2 block bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 bg-clip-text text-transparent">
                  Interactive Architectural Demo with VR, Cinematic & Map Features
                </span>
              </h1>

              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-gray-300 sm:text-xl">
                A high-end architectural walkthrough platform built by RedRender for Pixify. Featuring fully immersive
                <strong> VR walkthrough mode</strong>, <strong>20-scene cinematic camera tours</strong>, interactive
                <strong> Google Map geolocation</strong>, and smooth day/night transitions for world-class client presentations.
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/contact?service=Pixify%20Archi%20Walkthrough%20Demo%20Request"
                  className="group inline-flex items-center justify-center gap-3 rounded-full border border-pink-300/40 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-600 px-7 py-4 font-semibold text-white shadow-[0_0_30px_rgba(236,72,153,0.24)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_45px_rgba(236,72,153,0.38)]"
                >
                  Request Similar Demo
                  <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <a
                  href="https://pixify.co.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center gap-3 rounded-full border border-white/15 bg-white/5 px-7 py-4 font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-blue-300/40 hover:bg-white/10"
                >
                  Visit Pixify
                  <FaExternalLinkAlt className="text-sm transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </div>

              <div className="mt-8 flex flex-wrap gap-2.5">
                {[
                  'VR Walkthrough',
                  'Cinematic View (20 Scenes)',
                  'Google Map Location',
                  'Interactive Minimap',
                  'Day & Night Lighting',
                  'Unity 6 HDRP'
                ].map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-gray-200 backdrop-blur-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="animate-scale-in">
              <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.04] p-3 shadow-2xl shadow-pink-500/10 backdrop-blur-xl">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(236,72,153,0.18),transparent_34%),radial-gradient(circle_at_80%_90%,rgba(59,130,246,0.16),transparent_32%)]" />
                <div className="relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-black">
                  {isVideoLoaded ? (
                    <video
                      src={videoSrc}
                      className="aspect-video w-full bg-black object-cover"
                      controls
                      autoPlay
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsVideoLoaded(true)}
                      className="group relative flex aspect-video w-full items-center justify-center overflow-hidden bg-gradient-to-br from-slate-950 via-gray-900 to-black text-left"
                      aria-label="Play Pixify Archi Walkthrough demo video"
                    >
                      <video
                        src={videoSrc}
                        className="absolute inset-0 h-full w-full object-cover opacity-65 transition-transform duration-700 group-hover:scale-105"
                        muted
                        playsInline
                        preload="metadata"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-black/20" />
                      <span className="relative flex h-20 w-20 items-center justify-center rounded-full border border-pink-200/40 bg-pink-500/20 text-white shadow-2xl shadow-pink-500/30 backdrop-blur-md transition-all duration-300 group-hover:scale-110 group-hover:bg-pink-500/30">
                        <FaPlay className="ml-1 text-2xl" />
                      </span>
                      <span className="absolute bottom-5 left-5 rounded-full border border-white/15 bg-black/65 px-4 py-2 text-sm font-semibold text-white backdrop-blur-md">
                        Play architecture walkthrough demo
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CORE CAPABILITIES & KEY FEATURES */}
      <section className="relative">
        <div className="container relative z-10 mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="mb-12 max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-pink-400/30 bg-pink-400/10 px-5 py-2 backdrop-blur-sm">
              <FaCubes className="text-pink-200" />
              <span className="font-display text-sm font-semibold text-pink-200">Interactive Feature Suite</span>
            </div>
            <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
              Next-generation features built for immersive architectural presentation
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-gray-400">
              Designed from the ground up for luxury real estate developers, interior studios, and architectural agencies
              who want to present spatial flow, lighting mood, and physical scale with zero compromise.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {featureCards.map((feature, index) => (
              <div
                key={feature.title}
                className="group rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-7 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-pink-300/40 hover:shadow-2xl hover:shadow-pink-500/10 animate-scale-in"
                style={{ animationDelay: `${index * 0.07}s` }}
              >
                <div className="flex items-center justify-between">
                  <feature.icon className="text-4xl text-pink-300 transition-transform duration-300 group-hover:scale-110" />
                  <span className="rounded-full border border-pink-300/20 bg-pink-400/10 px-3 py-1 text-xs font-semibold text-pink-200">
                    {feature.badge}
                  </span>
                </div>
                <h3 className="mt-6 font-display text-2xl font-bold text-white">{feature.title}</h3>
                <p className="mt-3 leading-relaxed text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MULTI-INDUSTRY & SIMILAR PROJECT TYPES (InoviqSpace Style) */}
      <section className="relative">
        <div className="container relative z-10 mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="mb-12 max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-purple-400/30 bg-purple-400/10 px-5 py-2 backdrop-blur-sm">
              <FaProjectDiagram className="text-purple-200" />
              <span className="font-display text-sm font-semibold text-purple-200">Multi-Industry Applications</span>
            </div>
            <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
              Scalable across Hotels, Apartments, Hospitals & Commercial Projects
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-gray-400">
              This architectural walkthrough technology is not limited to luxury villas. The same high-performance Unity engine,
              VR capabilities, cinematic camera system, and Google Map integration provide massive commercial value across diverse project scales.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
            {industryUseCases.map((industry, index) => (
              <div
                key={industry.title}
                className="group relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-purple-400/40 hover:shadow-2xl hover:shadow-purple-500/10 animate-scale-in"
                style={{ animationDelay: `${index * 0.08}s` }}
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={industry.image}
                    alt={industry.imageAlt}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-transparent" />
                  <div className="absolute left-5 top-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-purple-200/30 bg-black/45 text-purple-200 shadow-xl shadow-black/30 backdrop-blur-md">
                    <industry.icon className="text-2xl transition-transform duration-300 group-hover:scale-110" />
                  </div>
                  <div className="absolute bottom-5 left-5 right-5">
                    <h3 className="font-display text-2xl font-bold text-white">{industry.title}</h3>
                    <p className="mt-1 text-sm font-semibold text-purple-200">Commercial walkthrough application</p>
                  </div>
                </div>

                <div className="p-7">
                  <div className="flex flex-wrap gap-2">
                    {industry.features.map((feature) => (
                      <span
                        key={feature}
                        className="rounded-full border border-purple-300/20 bg-purple-400/10 px-3 py-1 text-xs font-semibold text-purple-100"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 space-y-5">
                    <div>
                      <p className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-pink-300">
                        Market Need
                      </p>
                      <p className="mt-2 leading-relaxed text-gray-300">{industry.need}</p>
                    </div>

                    <div>
                      <p className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-blue-300">
                        Business Value
                      </p>
                      <p className="mt-2 leading-relaxed text-gray-300">{industry.value}</p>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
                      <p className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-purple-300">
                        Why Use It
                      </p>
                      <p className="mt-2 leading-relaxed text-gray-200">{industry.whyUse}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARKET VALUE & COMMERCIAL ROI (InoviqSpace Style) */}
      <section className="relative">
        <div className="container relative z-10 mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="mb-12 max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-pink-400/30 bg-pink-400/10 px-5 py-2 backdrop-blur-sm">
              <FaChartLine className="text-pink-200" />
              <span className="font-display text-sm font-semibold text-pink-200">Market Value & Commercial ROI</span>
            </div>
            <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
              Why interactive walkthroughs deliver unmatched business value
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-gray-400">
              Moving beyond static brochures and 2D floor plans enables developers, agencies, and architects to close deals
              faster, impress international investors, and eliminate expensive physical mockup constructions.
            </p>
          </div>

          <div className="grid gap-8 xl:grid-cols-3">
            {/* Card 1: Commercial Impact */}
            <div className="rounded-[2rem] border border-white/10 bg-gradient-to-br from-pink-500/10 via-white/[0.04] to-purple-500/10 p-8 backdrop-blur-xl sm:p-10">
              <div className="inline-flex items-center gap-2 rounded-full border border-pink-400/30 bg-pink-400/10 px-5 py-2">
                <FaChartLine className="text-pink-200" />
                <span className="font-display text-sm font-semibold text-pink-200">Commercial Impact</span>
              </div>
              <h2 className="mt-6 font-display text-3xl font-bold text-white sm:text-4xl">
                Tangible ROI clients can measure
              </h2>
              <div className="mt-7 space-y-4">
                {commercialOutcomes.map((item) => (
                  <div key={item} className="flex items-start gap-3 text-gray-200">
                    <FaCheckCircle className="mt-1 flex-shrink-0 text-pink-400" />
                    <span className="leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Card 2: Market Drivers */}
            <div className="rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-8 backdrop-blur-xl sm:p-10">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-400/30 bg-blue-400/10 px-5 py-2">
                <FaGlobeAmericas className="text-blue-200" />
                <span className="font-display text-sm font-semibold text-blue-200">Market Demand</span>
              </div>
              <h2 className="mt-6 font-display text-3xl font-bold text-white sm:text-4xl">
                Built for the modern real estate market
              </h2>
              <div className="mt-7 space-y-5">
                {marketDrivers.map((driver) => (
                  <div key={driver.title} className="flex gap-4">
                    <driver.icon className="mt-1 flex-shrink-0 text-blue-300" />
                    <div>
                      <h3 className="font-display text-lg font-bold text-white">{driver.title}</h3>
                      <p className="mt-1 leading-relaxed text-gray-400">{driver.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Card 3: Technical Capabilities */}
            <div className="rounded-[2rem] border border-white/10 bg-gradient-to-br from-purple-500/10 via-white/[0.04] to-blue-500/10 p-8 backdrop-blur-xl sm:p-10">
              <div className="inline-flex items-center gap-2 rounded-full border border-purple-400/30 bg-purple-400/10 px-5 py-2">
                <FaDesktop className="text-purple-200" />
                <span className="font-display text-sm font-semibold text-purple-200">Platform Specs</span>
              </div>
              <h2 className="mt-6 font-display text-3xl font-bold text-white sm:text-4xl">
                High-performance native execution
              </h2>
              <p className="mt-4 leading-relaxed text-gray-400">
                Native engine architecture allows massive interior geometry, ray-traced shadows, and real-time VR playback without lag.
              </p>
              <div className="mt-7 space-y-3">
                {technicalCapabilities.map((spec) => (
                  <div key={spec} className="flex items-center gap-3 text-sm text-gray-200">
                    <FaLightbulb className="flex-shrink-0 text-purple-300" />
                    <span>{spec}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CALL TO ACTION */}
      <section className="relative">
        <div className="container relative z-10 mx-auto px-4 py-16 pb-20 sm:px-6 sm:py-20 lg:px-8">
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-pink-500/15 via-white/[0.04] to-blue-500/10 px-6 py-12 text-center shadow-2xl shadow-black/20 backdrop-blur-xl sm:px-10 sm:py-16">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(236,72,153,0.22),_transparent_55%)]" />
            <div className="relative">
              <img
                src={pixifyLogo}
                alt="Pixify Design Agency"
                className="mx-auto w-full max-w-[16rem] object-contain sm:max-w-xs"
              />
              <h2 className="mt-6 font-display text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
                Ready to bring your architectural projects to life?
              </h2>
              <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-gray-300">
                Whether you are developing a luxury hotel, residential apartment tower, healthcare campus, or commercial space,
                RedRender can build custom VR walkthroughs, cinematic tours, and interactive maps tailored to your exact project.
              </p>
              <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
                <Link
                  to="/contact?service=Pixify%20Archi%20Walkthrough%20Demo%20Request"
                  className="group inline-flex items-center justify-center gap-3 rounded-full border border-pink-300/40 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-600 px-8 py-4 font-semibold text-white shadow-[0_0_30px_rgba(236,72,153,0.24)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_45px_rgba(236,72,153,0.38)]"
                >
                  Request Similar Demo
                  <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <a
                  href="https://pixify.co.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center gap-3 rounded-full border border-white/15 bg-white/5 px-8 py-4 font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-blue-300/40 hover:bg-white/10"
                >
                  Visit Pixify
                  <FaExternalLinkAlt className="text-sm transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PixifyArchiWalkthrough;

